import { EligibleFilesTable } from "@/components/reconciliation/eligible-files-table";
import { reconciliationMessages } from "@/services/messages";
import type {
	CreateReconciliationSetRequest,
	ReconciliationMode,
	ReconciliationSet,
	UpdateReconciliationSetRequest,
} from "@efes/shared";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface SetFormProps {
	/** Если задано — форма работает в режиме редактирования. */
	initialValue?: ReconciliationSet;
	/**
	 * Submit «Сохранить». После успешного сохранения caller (create.tsx)
	 * прогоняет pre-flight в фоне, чтобы сразу показать issues если есть.
	 * Статус набора не меняется — он остаётся `draft` до запуска сверки.
	 */
	onSave: (
		input: CreateReconciliationSetRequest | UpdateReconciliationSetRequest,
	) => void | Promise<void>;
	/**
	 * «Отмена» — пользователь хочет уйти без сохранения. Caller решает
	 * куда вернуться: list (для create-режима) или detail (для edit).
	 */
	onCancel: () => void;
	/** Если true — форма заблокирована (мутация в процессе). */
	isPending?: boolean;
}

/**
 * Конфиг чекбоксов режимов в раскрываемой секции.
 *
 * Порядок: cost → volume (дефолт) → rate (advanced). Соответствует
 * частоте использования на production-данных (Wave 6 discovery).
 */
const MODES: Array<{
	value: ReconciliationMode;
	labelKey: keyof typeof reconciliationMessages;
	descriptionKey: keyof typeof reconciliationMessages;
	warningKey?: keyof typeof reconciliationMessages;
}> = [
	{ value: "cost", labelKey: "mode_cost", descriptionKey: "mode_cost_description" },
	{ value: "volume", labelKey: "mode_volume", descriptionKey: "mode_volume_description" },
	{
		value: "rate",
		labelKey: "mode_rate",
		descriptionKey: "mode_rate_description",
		warningKey: "mode_rate_warning",
	},
];

const DEFAULT_MODES: ReconciliationMode[] = ["cost", "volume"];

/**
 * Дефолтное имя для нового набора: «Сверка от ДД.ММ.ГГГГ ЧЧ:ММ» в локали ru-RU.
 * Пользователь может перезаписать при создании; pre-fill снимает «зависание»
 * на пустом поле имени, когда пользователь хочет быстро нажать "Сохранить".
 */
function buildDefaultName(): string {
	const now = new Date();
	const formatted = now.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
	return `Сверка от ${formatted}`;
}

/** True, если выбранный набор режимов отличается от дефолтного (по составу). */
function isCustomModes(modes: readonly ReconciliationMode[]): boolean {
	if (modes.length !== DEFAULT_MODES.length) return true;
	const set = new Set(modes);
	return DEFAULT_MODES.some((m) => !set.has(m));
}

/**
 * Универсальная форма создания и редактирования набора сверки.
 *
 * Поля: имя, режимы (в раскрываемой секции), два списка файлов (наряды, акты).
 * Клиентская валидация — минимум 1 наряд и 1 акт (FR-019, SC-008).
 *
 * Фича 026 / Sprint 3 / A4:
 * - Уровень детализации убран — pipeline всегда выдаёт unified-отчёт. Поле
 *   `level` больше не отправляется в payload (backend default = "unified").
 * - Wave 6: режимы в раскрываемой секции «Дополнительные настройки». Дефолт
 *   для новых наборов — `["cost", "volume"]`. Секция автоматически раскрыта
 *   при редактировании набора с нестандартной комбинацией режимов.
 *
 * Одна submit-кнопка «Сохранить». Раньше было две («Сохранить как черновик»
 * и «Сохранить и подготовить к запуску»), но после упразднения статуса
 * `ready` обе сохраняли как `draft` — отличие было только в том, что вторая
 * вызывала pre-flight, чтобы сразу показать issues. Теперь pre-flight
 * автоматически идёт после save в caller'е (create.tsx).
 */
export function SetForm({
	initialValue,
	onSave,
	onCancel,
	isPending,
}: SetFormProps) {
	// initializer-функция → buildDefaultName вычисляется один раз на mount.
	const [name, setName] = useState<string>(() => initialValue?.name ?? buildDefaultName());
	// Sprint 3 / A4: level больше не отправляется в payload — backend
	// использует default "unified" (Zod-схема CreateReconciliationSetRequest).
	const [modes, setModes] = useState<ReconciliationMode[]>(
		initialValue?.modes ?? DEFAULT_MODES,
	);
	const [workOrderIds, setWorkOrderIds] = useState<number[]>(
		initialValue?.files.workOrders.map((f) => f.id) ?? [],
	);
	const [actIds, setActIds] = useState<number[]>(initialValue?.files.acts.map((f) => f.id) ?? []);

	const minFilesOk = workOrderIds.length >= 1 && actIds.length >= 1;
	const nameOk = name.trim().length > 0;
	const modesOk = modes.length >= 1;
	const isValid = minFilesOk && nameOk && modesOk;

	const buildPayload = (): CreateReconciliationSetRequest => ({
		name: name.trim(),
		// Sprint 3 / A4: уровень всегда `unified` (zombie aggregate-by-work-kind удалён).
		// Schema `optional().default("unified")` означает, что поле опционально на входе,
		// но `z.infer` показывает заполненное значение в output-типе — отсюда явная подача.
		level: "unified",
		modes,
		files: {
			workOrders: workOrderIds,
			acts: actIds,
		},
	});

	const toggleMode = (mode: ReconciliationMode) => {
		setModes((prev) => (prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!isValid || isPending) return;
		void onSave(buildPayload());
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			{/* Имя */}
			<div className="space-y-1">
				<label htmlFor="rec-set-name" className="block text-sm font-medium text-slate-700">
					{reconciliationMessages.form_label_name}
				</label>
				<input
					id="rec-set-name"
					type="text"
					value={name}
					onChange={(e) => setName(e.target.value)}
					maxLength={200}
					placeholder={reconciliationMessages.form_placeholder_name}
					disabled={isPending}
					className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500"
				/>
			</div>

			{/* Дополнительные настройки (Wave 6): свёрнуто по умолчанию для
			    новых наборов, раскрыто при нестандартной комбинации режимов. */}
			<details
				className="group rounded-lg border border-slate-200 bg-white"
				open={isCustomModes(modes)}
			>
				<summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
					<span className="flex items-center gap-2">
						<span className="text-slate-400 transition-transform group-open:rotate-90">▸</span>
						{reconciliationMessages.form_advanced_summary}
					</span>
					<span className="text-xs font-normal text-slate-500">
						{isCustomModes(modes)
							? reconciliationMessages.form_advanced_hint_custom
							: reconciliationMessages.form_advanced_hint_default}
					</span>
				</summary>
				<div className="space-y-2 border-t border-slate-200 p-3">
					<div className="block text-sm font-medium text-slate-700">
						{reconciliationMessages.form_label_modes}
					</div>
					<div className="space-y-2">
						{MODES.map((opt) => {
							const checked = modes.includes(opt.value);
							return (
								<label
									key={opt.value}
									className={`flex items-start gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${
										checked
											? "border-blue-500 bg-blue-50"
											: "border-slate-200 bg-white hover:bg-slate-50"
									}`}
								>
									<input
										type="checkbox"
										checked={checked}
										onChange={() => toggleMode(opt.value)}
										disabled={isPending}
										className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
									/>
									<div className="flex-1">
										<div
											className={`font-medium ${checked ? "text-blue-700" : "text-slate-700"}`}
										>
											{reconciliationMessages[opt.labelKey]}
										</div>
										<div className="text-xs text-slate-500">
											{reconciliationMessages[opt.descriptionKey]}
										</div>
										{opt.warningKey && (
											<div className="text-xs italic text-amber-600">
												{reconciliationMessages[opt.warningKey]}
											</div>
										)}
									</div>
								</label>
							);
						})}
					</div>
					{!modesOk && (
						<div className="text-xs text-red-600">
							Выберите минимум один режим сверки.
						</div>
					)}
				</div>
			</details>

			{/* Наряды */}
			<div className="space-y-2">
				<div className="block text-sm font-medium text-slate-700">
					{reconciliationMessages.form_label_workorders}
				</div>
				<EligibleFilesTable
					fileRole="work_order"
					selectedFileIds={workOrderIds}
					onChange={setWorkOrderIds}
				/>
			</div>

			{/* Акты */}
			<div className="space-y-2">
				<div className="block text-sm font-medium text-slate-700">
					{reconciliationMessages.form_label_acts}
				</div>
				<EligibleFilesTable fileRole="act" selectedFileIds={actIds} onChange={setActIds} />
			</div>

			{/* Inline валидация */}
			{!minFilesOk && (
				<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
					{reconciliationMessages.form_min_files_warning}
				</div>
			)}

			{/* Кнопки. Сохранить — единственная primary; Отмена возвращает caller'а
			    на предыдущий экран (list для create, detail для edit). */}
			<div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-4">
				<button
					type="button"
					onClick={onCancel}
					disabled={isPending}
					className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{reconciliationMessages.button_cancel}
				</button>
				<button
					type="submit"
					disabled={!isValid || isPending}
					className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					{reconciliationMessages.button_save}
				</button>
			</div>
		</form>
	);
}
